/**
 * Step 11 — the client deck (prototype `VIEWS.deck`). Generate (POST /clients/{id}/decks), read back
 * (GET /client-decks/{id}) and download as PDF or PowerPoint (GET /client-decks/{id}/file → a same-origin link
 * served as an attachment). The deck is a snapshot, so the preview and both downloads show the same numbers.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateDeckBody, DeckFormat, DeckPeriod } from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';

export const deckKey = (id: string) => ['client-deck', id] as const;

/** GET /client-decks/{id}: a deck generated earlier (the preview after a reload or a shared link). */
export function useDeck(id: string | null) {
  return useQuery({
    queryKey: deckKey(id ?? ''),
    queryFn: () => call('decks.get', { params: { id: id! } }),
    enabled: !!id,
  });
}

/** POST /clients/{id}/decks: builds the slides now and stores them (audited). */
export function useCreateDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; body: CreateDeckBody }) =>
      call('decks.create', { params: { id: v.clientId }, body: v.body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: (d) => qc.setQueryData(deckKey(d.id), d),
  });
}

/**
 * Download PDF / Download PowerPoint. The link is same-origin, so the `download` attribute saves the file under the
 * deck's name and leaves this page where it is (the file store serves a PDF inline, which would otherwise open it in
 * place of the app).
 */
export async function downloadDeck(id: string, format: DeckFormat): Promise<void> {
  const link = await call('decks.file', { params: { id }, query: { format } });
  const a = document.createElement('a');
  a.href = link.url;
  a.download = link.filename ?? '';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * The prototype's period select (docs/prototype.html L4373, its today 5 Oct 2026: "September 2026", "Q3 2026"): the
 * last COMPLETED month and the last COMPLETED quarter, in IST — a review is of a period that has ended (PA, 16 Oct).
 * The month comes first, so it is the default. In January both roll back a year (December and Q4).
 */
export function deckPeriods(now = new Date()): DeckPeriod[] {
  const [ys, ms] = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit' }).format(now).split('-') as [string, string];
  const y = Number(ys), m = Number(ms);
  const month = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
  const q = Math.floor((m - 1) / 3) + 1;
  const quarter = q === 1 ? `${y - 1}-Q4` : `${y}-Q${q - 1}`;
  return [month, quarter];
}
